import { useState } from 'react';

import { EditIcon, ExternalLinkIcon, XIcon } from 'lucide-react';

import { Button } from './ui/button';
import { FormControl, FormItem, FormLabel, FormMessage } from './ui/form';
import { Input } from './ui/input';

/**
 * @typedef {Object} ImageInputWithPreviewProps
 * @property {string} label - The label text for the field
 * @property {Object} dimensions - The image dimensions
 * @property {number} dimensions.width - Image width
 * @property {number} dimensions.height - Image height
 * @property {string} fileFieldName - The form field name for the file
 * @property {string} filenameFieldName - The form field name for the filename
 * @property {string} currentFilename - The current filename to display
 * @property {string} imageUrl - The URL of the image to preview
 * @property {(file: File, filename: string) => void} onFileChange - Callback when file changes
 */

/**
 * Reusable image field component with preview and edit functionality
 * @param {ImageInputWithPreviewProps} props
 */
export const ImageInputWithPreview = ({
  label,
  dimensions,
  fileFieldName,
  filenameFieldName,
  currentFilename,
  imageUrl,
  onFileChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  /**
   * @param {React.ChangeEvent<HTMLInputElement>} ev
   */
  const handleFileChange = (ev) => {
    const files = ev.target.files;
    if (!files?.length) return;
    const file = files[0];
    onFileChange(file, file.name);
  };

  if (isEditing) {
    return (
      <FormItem className="col-span-1 md:col-span-2">
        <FormLabel>
          {label} ({dimensions.width}x{dimensions.height})
        </FormLabel>

        <FormControl>
          <Input type="file" onChange={handleFileChange} />
        </FormControl>

        <FormMessage />

        <Button
          className="mt-6"
          variant="destructive"
          onClick={() => setIsEditing(false)}
        >
          <XIcon />
          Cancelar Edição
        </Button>
      </FormItem>
    );
  }

  return (
    <FormItem className="col-span-1 md:col-span-2">
      <FormLabel>
        {label} ({dimensions.width}x{dimensions.height})
      </FormLabel>

      <div className="ring-offset-backgroundfocus-visible:outline-none flex h-auto items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
        {currentFilename}

        <div>
          <a href={imageUrl} target="_blank" rel="noreferrer noopener">
            <Button
              type="button"
              className="h-auto p-2 text-xs"
              variant="ghost"
            >
              <ExternalLinkIcon className="size-4" />
              Ver Imagem
            </Button>
          </a>

          <Button
            className="h-auto p-2 text-xs"
            variant="ghost"
            onClick={() => setIsEditing(true)}
          >
            <EditIcon />
            Editar
          </Button>
        </div>
      </div>

      <FormMessage />
    </FormItem>
  );
};
